import type { LucideIcon } from 'lucide-react';
import { Search, Activity, Boxes, Workflow, Network, ShieldCheck, ArrowRight } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { LocalizedAnchor } from '@/components/LocalizedLink';

interface Feature {
  key: string;
  icon: LucideIcon;
  /** Dedicated page or doc that carries the technical depth. */
  href: string;
}

/**
 * Capability overview at decision-maker altitude: each card says what the
 * capability does for the business in one or two plain sentences, then hands
 * off to a dedicated page for the technical detail.
 */
const features: Feature[] = [
  { key: 'serviceIntelligence', icon: Network, href: '/service-intelligence' },
  { key: 'anomaly', icon: Activity, href: '/docs/anomaly-detection/overview' },
  { key: 'search', icon: Search, href: '/ai-log-search' },
  { key: 'siem', icon: ShieldCheck, href: '/siem' },
  { key: 'templates', icon: Boxes, href: '/docs/integrations/cloud-setup' },
  { key: 'pipelines', icon: Workflow, href: '/docs/pipelines/building-pipelines' },
];

export function FeaturesSection() {
  const t = useT();

  return (
    <section id="features" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mb-14 sm:mb-16 animate-on-scroll">
          <p className="eyebrow">{t('features.eyebrow')}</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
            {t('features.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Capability cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <LocalizedAnchor
                key={feature.key}
                href={feature.href}
                className="animate-on-scroll group rounded-xl p-7 flex flex-col bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 hover:border-primary-300 dark:hover:border-primary-500/40 transition-colors"
              >
                <Icon className="h-6 w-6 text-gray-400 dark:text-gray-500 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" aria-hidden="true" />

                <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                  {t(`features.${feature.key}.title`)}
                </h3>

                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                  {t(`features.${feature.key}.description`)}
                </p>

                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400">
                  {t('features.learnMore')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </LocalizedAnchor>
            );
          })}
        </div>
      </div>
    </section>
  );
}
